import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v2AdminPauseInvestorContactMutation } from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { Input } from '@workspace/ui/components/input'
import { useState } from 'react'

/**
 * Per-contact outreach pause.
 *
 * The fund-level equivalent is `investor.inactiveUntil`, shown in the Outreach
 * Status card. This one exists for the case where pausing the fund would be
 * wrong: a reply that speaks for a different fund than the contact is filed
 * under, or a real mandate we cannot serve yet. Pausing the investor there
 * would freeze a fund that has done nothing wrong.
 */

/** `<input type="date">` speaks YYYY-MM-DD; anything else renders blank. */
export const toDateInputValue = (value: string | null | undefined): string =>
  value ? new Date(value).toISOString().slice(0, 10) : ''

/**
 * Read the date back as end-of-day UTC. A bare YYYY-MM-DD parses as midnight,
 * which would end the pause a day early — "paused until the 10th" reads as
 * inclusive of the 10th.
 */
export const fromDateInputValue = (value: string): string =>
  new Date(`${value}T23:59:59.999Z`).toISOString()

/**
 * A pause only suppresses outreach while its date is in the future. An elapsed
 * one is left in place deliberately — nothing clears it, and it doubles as a
 * record of why the contact was held.
 */
export const isPauseActive = (pausedUntil: string | null | undefined, now = new Date()): boolean =>
  Boolean(pausedUntil && new Date(pausedUntil) > now)

/**
 * Body for the pause endpoint. Clearing the pause clears its reason too, so a
 * stale explanation cannot outlive the hold it described, and a blank reason is
 * sent as null rather than an empty string.
 */
export function buildPausePayload(
  until: string | null,
  reason: string
): { pausedUntil: string | null; pausedReason: string | null } {
  return {
    pausedUntil: until,
    pausedReason: until ? reason.trim() || null : null,
  }
}

/**
 * Rendered in UTC, deliberately.
 *
 * The pause is stored as end-of-day UTC and read back into the date input with
 * toISOString(), which is also UTC. Formatting in local time made those three
 * disagree: east of Greenwich, 2030-04-17T23:59:59.999Z renders as "Apr 18",
 * so an admin picked the 17th, saw the 18th on the badge, and saw the 17th
 * again the moment they reopened the editor.
 */
export const formatDate = (value: string | null | undefined): string =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—'

interface Props {
  contactId: string
  pausedUntil: string | null
  pausedReason: string | null
  /** Excluded contacts are suppressed permanently, so a pause is meaningless. */
  isExcluded: boolean
}

export function ContactPauseControl({ contactId, pausedUntil, pausedReason, isExcluded }: Props) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [date, setDate] = useState(() => toDateInputValue(pausedUntil))
  const [reason, setReason] = useState(pausedReason ?? '')

  const pause = useMutation({
    ...v2AdminPauseInvestorContactMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries()
      setEditing(false)
    },
  })

  if (isExcluded) return null

  const isActive = isPauseActive(pausedUntil)

  const save = (until: string | null) =>
    pause.mutate({ path: { id: contactId }, body: buildPausePayload(until, reason) })

  if (!editing) {
    return (
      <div className="mt-1 flex items-center gap-2">
        {isActive ? (
          <>
            <Badge variant="warning" size="sm">
              Paused until {formatDate(pausedUntil)}
            </Badge>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline"
              onClick={() => setEditing(true)}
            >
              Change
            </button>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline"
              disabled={pause.isPending}
              onClick={() => save(null)}
            >
              Resume
            </button>
          </>
        ) : (
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground underline"
            onClick={() => setEditing(true)}
          >
            Pause outreach
          </button>
        )}
        {isActive && pausedReason && (
          <span className="text-xs text-muted-foreground truncate">· {pausedReason}</span>
        )}
      </div>
    )
  }

  return (
    <div className="mt-1 flex flex-col gap-1.5">
      <Input
        type="date"
        className="h-8 text-xs"
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />
      <Input
        type="text"
        placeholder="Reason (optional)"
        className="h-8 text-xs"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!date || pause.isPending}
          onClick={() => save(fromDateInputValue(date))}
        >
          {pause.isPending ? '…' : 'Save'}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
      {pause.isError && <span className="text-xs text-destructive">Could not save the pause.</span>}
    </div>
  )
}
